/* global axios */
// PowerAgents API client (BUILDER-04 / M4).
// Uses the global axios instance (carries the DeviseTokenAuth session headers set on
// login) — a bare fetch() sends no auth headers and 401s. Returns the parsed body (r.data).
// Contracts: docs/specs/active/BUILDER-02-schema-crud/CONTRACTS.md
// All routes are account-scoped, gated to platform Super Admins (any account).

const BASE = accountId => `/api/v1/accounts/${accountId}/power_agents`;
const TOOLS_BASE = accountId =>
  `/api/v1/accounts/${accountId}/power_agent_tools`;

const body = r => r.data;

export default {
  // ── Agents CRUD (§1) ──────────────────────────────────────────
  index(accountId) {
    return axios.get(BASE(accountId)).then(body);
  },
  // Catalogo de modelos: curadoria no servidor, numeros vivos da OpenRouter (cache 6h).
  // Devolve [] em vez de estourar — a tela de modelos tem fallback estatico e nao pode
  // deixar de abrir porque uma API de terceiro caiu.
  models(accountId) {
    return axios
      .get(`${BASE(accountId)}/models`)
      .then(r => r.data?.payload || [])
      .catch(() => []);
  },
  show(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}`).then(body);
  },
  create(accountId, data) {
    return axios.post(BASE(accountId), data).then(body);
  },
  update(accountId, id, data) {
    return axios.patch(`${BASE(accountId)}/${id}`, data).then(body);
  },
  destroy(accountId, id) {
    return axios.delete(`${BASE(accountId)}/${id}`).then(body);
  },

  // ── Tool bindings (§2) ────────────────────────────────────────
  toolsIndex(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/tools`).then(body);
  },
  toolsCreate(accountId, id, data) {
    return axios.post(`${BASE(accountId)}/${id}/tools`, data).then(body);
  },
  toolsUpdate(accountId, id, bindingId, data) {
    return axios
      .patch(`${BASE(accountId)}/${id}/tools/${bindingId}`, data)
      .then(body);
  },
  toolsDestroy(accountId, id, bindingId) {
    return axios
      .delete(`${BASE(accountId)}/${id}/tools/${bindingId}`)
      .then(body);
  },
  // Dry-run a tool with sample params before saving (#3b). Secrets stay server-side.
  toolTest(accountId, id, data) {
    return axios.post(`${BASE(accountId)}/${id}/tools/test`, data).then(body);
  },

  // ── Guardrails (§3) ───────────────────────────────────────────
  guardrailsIndex(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/guardrails`).then(body);
  },
  guardrailsUpsert(accountId, id, guardrailType, data) {
    return axios
      .put(`${BASE(accountId)}/${id}/guardrails/${guardrailType}`, data)
      .then(body);
  },
  // Testar o rail contra um texto colado, sem salvar nada e sem ligar nada.
  guardrailTest(accountId, id, guardrailType, data) {
    return axios
      .post(`${BASE(accountId)}/${id}/guardrails/${guardrailType}/test`, data)
      .then(body);
  },

  // ── Routing (§4) ──────────────────────────────────────────────
  routingShow(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/routing`).then(body);
  },
  routingUpdate(accountId, id, routing) {
    return axios
      .put(`${BASE(accountId)}/${id}/routing`, { routing })
      .then(body);
  },

  // ── Prompt versions (§5) ──────────────────────────────────────
  promptVersionsIndex(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/prompt-versions`).then(body);
  },
  promptVersionCreate(accountId, id, data) {
    return axios
      .post(`${BASE(accountId)}/${id}/prompt-versions`, data)
      .then(body);
  },
  promptVersionShow(accountId, id, versionId) {
    return axios
      .get(`${BASE(accountId)}/${id}/prompt-versions/${versionId}`)
      .then(body);
  },
  promptVersionRestore(accountId, id, versionId) {
    return axios
      .post(`${BASE(accountId)}/${id}/prompt-versions/${versionId}/restore`)
      .then(body);
  },

  // ── Channel (§8) ──────────────────────────────────────────────
  channel(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/channel`).then(body);
  },
  // `force` reatribui um inbox que já responde por outro agente. O backend recusa por
  // padrão de propósito: sem isso, conectar aqui faria o outro agente parar de receber
  // webhook em silêncio.
  connectInbox(accountId, id, inboxId, force = false) {
    return axios
      .post(`${BASE(accountId)}/${id}/connect_inbox`, {
        inbox_id: inboxId,
        ...(force ? { force: true } : {}),
      })
      .then(body);
  },
  disconnectInbox(accountId, id, inboxId) {
    return axios
      .post(`${BASE(accountId)}/${id}/disconnect_inbox`, { inbox_id: inboxId })
      .then(body);
  },

  // ── Publish (§7) ──────────────────────────────────────────────
  publish(accountId, id) {
    return axios.post(`${BASE(accountId)}/${id}/publish`).then(body);
  },
  // AGENTS-PLAYGROUND-01: o teste do RASCUNHO, sem caixa conectada e sem publicar. O envio
  // devolve o pedido; a resposta sai numa consulta, porque o turno com ferramentas passa dos
  // 15 s que o servidor dá a uma requisição.
  // Com `audioId`, o turno é a nota de voz: o servidor põe nele a transcrição que ELE guardou, e
  // o texto que a tela mostra não viaja de volta.
  // `anexos` são as referências que `playgroundFilesUpload` devolveu, uma por foto ou PDF.
  playgroundDraft(
    accountId,
    id,
    sessionId,
    content,
    audioId = null,
    anexos = []
  ) {
    return axios
      .post(`${BASE(accountId)}/${id}/playground_draft`, {
        session_id: sessionId,
        ...(audioId ? { audio_id: audioId } : { content }),
        ...(anexos.length ? { anexos } : {}),
      })
      .then(body);
  },
  // Foto e PDF no Playground (L-23): sobem antes do turno, e a resposta traz, por arquivo, a
  // referência (`id`) que o turno leva. As regras são as da página do link.
  playgroundFilesUpload(accountId, id, arquivos) {
    const form = new FormData();
    arquivos.forEach(arquivo => form.append('anexos[]', arquivo));
    return axios
      .post(`${BASE(accountId)}/${id}/playground/anexos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(body);
  },
  // A nota de voz do Playground, em duas etapas: sobe o áudio e consulta a transcrição, que
  // passa dos 15 s de uma requisição. `duracao` vai em segundos e entra na estimativa de custo
  // do teto de testes.
  playgroundAudioUpload(accountId, id, arquivo, duracaoMs) {
    const form = new FormData();
    form.append('audio', arquivo);
    form.append('duracao', String(duracaoMs / 1000));
    return axios
      .post(`${BASE(accountId)}/${id}/playground/audios`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(body);
  },
  playgroundAudioResult(accountId, id, audioId) {
    return axios
      .get(`${BASE(accountId)}/${id}/playground/audios/${audioId}`)
      .then(body);
  },
  playgroundDraftResult(accountId, id, requestId) {
    return axios
      .get(`${BASE(accountId)}/${id}/playground_draft/${requestId}`)
      .then(body);
  },
  playgroundDraftReset(accountId, id, sessionId) {
    return axios
      .delete(`${BASE(accountId)}/${id}/playground_draft_session`, {
        data: { session_id: sessionId },
      })
      .then(body);
  },

  // ── Playground no modo canal (AGENTS-LINK-01, L-27) ───────────
  // A conversa é de verdade, na caixa de teste do agente, como a da página do link no modo canal. O
  // envio e o "Nova conversa" são as rotas antigas do Playground, que já escrevem na caixa de teste
  // nesse modo, e dizem o modo da tela; as leituras são só do canal. Se o agente trocou de modo com a
  // tela aberta, o servidor responde 409 `modo_trocado` em vez de escrever no destino do motor.
  // O estado é o que a tela precisa para abrir: a caixa, o que a versão publicada decide e a conversa.
  playgroundCanalEstado(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/playground/canal`).then(body);
  },
  // O texto, a foto e o PDF, e a nota de voz com os arquivos dela: tudo vira uma mensagem da caixa,
  // com os arquivos anexados, como a do widget.
  playgroundCanalEnviar(accountId, id, content, arquivos = []) {
    const rota = `${BASE(accountId)}/${id}/playground`;
    if (!arquivos.length)
      return axios.post(rota, { content, modo: 'canal' }).then(body);
    const form = new FormData();
    form.append('modo', 'canal');
    if (content) form.append('content', content);
    arquivos.forEach(arquivo => form.append('attachments[]', arquivo));
    return axios
      .post(rota, form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(body);
  },
  // As respostas que chegaram depois da mensagem `depois`, na conversa viva de quem testa e nas
  // `conversas` que a tela ainda mostra (os números que a abertura e o envio devolveram).
  playgroundCanalNovas(accountId, id, depois, conversas = []) {
    return axios
      .get(`${BASE(accountId)}/${id}/playground/mensagens`, {
        params: { depois, conversas },
      })
      .then(body);
  },
  // A transcrição da nota de voz, que o job de produção grava no anexo da mensagem.
  playgroundCanalAudio(accountId, id, mensagemId) {
    return axios
      .get(`${BASE(accountId)}/${id}/playground/mensagens/${mensagemId}/audio`)
      .then(body);
  },
  playgroundCanalRecomecar(accountId, id) {
    return axios
      .post(`${BASE(accountId)}/${id}/playground_reset`, { modo: 'canal' })
      .then(body);
  },

  // ── Link de teste (AGENTS-LINK-01) ────────────────────────────
  // A página pública onde qualquer pessoa conversa com a versão PUBLICADA do agente. Tudo aqui
  // é do super admin. O `show` junta o link com o que é do runtime (ferramentas publicadas e
  // gasto do dia), e cada parte do runtime volta com o motivo em `error` quando falta.
  testLink(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/test_link`).then(body);
  },
  // Gera, ou gera outro revogando o anterior. Sem `expira_em`, o novo vale 7 dias.
  testLinkCreate(accountId, id, data = {}) {
    return axios.post(`${BASE(accountId)}/${id}/test_link`, data).then(body);
  },
  testLinkUpdate(accountId, id, data) {
    return axios.patch(`${BASE(accountId)}/${id}/test_link`, data).then(body);
  },
  testLinkRevoke(accountId, id) {
    return axios.post(`${BASE(accountId)}/${id}/test_link/revogar`).then(body);
  },
  testLinkSessions(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/test_link/sessoes`).then(body);
  },
  testLinkTurns(accountId, id, sessionId) {
    return axios
      .get(`${BASE(accountId)}/${id}/test_link/sessoes/${sessionId}/turnos`)
      .then(body);
  },
  // O modo do Playground é coluna do agente, fora do rascunho: trocar não pede publicação.
  playgroundModeUpdate(accountId, id, mode) {
    return axios
      .put(`${BASE(accountId)}/${id}/playground_mode`, {
        playground_mode: mode,
      })
      .then(body);
  },

  // ── Secrets (#11) ─────────────────────────────────────────────
  // Masked view of this agent's keys + keys reusable from other agents.
  secretsIndex(accountId, id) {
    return axios.get(`${BASE(accountId)}/${id}/secrets`).then(body);
  },
  // `secrets` carries raw new values; `reuse` maps a key to a source agent id to
  // copy its stored value server-side. Both are write-only (reads stay masked).
  secretsUpdate(accountId, id, data) {
    return axios.put(`${BASE(accountId)}/${id}/secrets`, data).then(body);
  },

  // Prova a chave mandando um áudio de um segundo e vendo se transcreve. Não é consulta de
  // saldo: só a OpenRouter publica saldo, e um selo verde que significasse coisas diferentes
  // em cada provedor seria pior que nenhum. O 200 é a única resposta que o provedor dá depois
  // de autenticar E debitar.
  secretsValidate(accountId, id, data) {
    return axios
      .post(`${BASE(accountId)}/${id}/secrets/validate`, data)
      .then(body);
  },
  // #2 (wave5): the org's n8n OpenRouter credentials (id + name) + this agent's selection.
  // Bind an existing n8n credential (pass credential_id/credential_name), create one from a pasted
  // key (pass name + api_key), or mint a fresh key (pass name only). Returns the selected credential.
  // Delete an n8n credential (instance-wide). n8n has no update, so a key change is delete + recreate.

  // ── Bases de conhecimento (PowerData) ─────────────────────────
  // A ligação agente↔base é N:N e mora em `power_data_connections`, chaveada pelo SLUG do
  // agente — é por ele que o retrieve do PowerData resolve TODAS as bases conectadas e busca
  // no conjunto. Por isso ligar a segunda base não exige nada além de uma segunda conexão.
  knowledgeStores(accountId) {
    return axios.get(`/api/v1/accounts/${accountId}/power_data`).then(body);
  },
  knowledgeOfAgent(accountId, slug) {
    return axios
      .get(`/api/v1/accounts/${accountId}/power_data/agent/${slug}/stores`)
      .then(body);
  },
  knowledgeConnect(accountId, storeId, slug) {
    return axios
      .post(`/api/v1/accounts/${accountId}/power_data/${storeId}/connections`, {
        agent_id: slug,
      })
      .then(body);
  },
  knowledgeDisconnect(accountId, storeId, slug) {
    return axios
      .delete(
        `/api/v1/accounts/${accountId}/power_data/${storeId}/connections/${slug}`
      )
      .then(body);
  },

  // ── Foto do agente ────────────────────────────────────────────
  // É o avatar do AgentBot: o bot já tem anexo, URL e endpoint de purge. Multipart, então
  // sem `body()` do axios padrão — a resposta traz o bot inteiro.
  uploadAvatar(accountId, agentBotId, file) {
    const form = new FormData();
    form.append('avatar', file);
    return axios
      .patch(`/api/v1/accounts/${accountId}/agent_bots/${agentBotId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(body);
  },
  deleteAvatar(accountId, agentBotId) {
    return axios
      .delete(`/api/v1/accounts/${accountId}/agent_bots/${agentBotId}/avatar`)
      .then(body);
  },

  // ── Tool catalog (§6) ─────────────────────────────────────────
  catalogIndex(accountId) {
    return axios.get(TOOLS_BASE(accountId)).then(body);
  },
  catalogCreate(accountId, data) {
    return axios.post(TOOLS_BASE(accountId), data).then(body);
  },
  catalogUpdate(accountId, toolId, data) {
    return axios.patch(`${TOOLS_BASE(accountId)}/${toolId}`, data).then(body);
  },
  catalogDestroy(accountId, toolId) {
    return axios.delete(`${TOOLS_BASE(accountId)}/${toolId}`).then(body);
  },
};
